/* eslint-disable react-hooks/exhaustive-deps */
// hooks/province/useProvince.js
import { useCallback, useMemo } from 'react';
import { useProvinceContext } from './context';
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import Swal from 'sweetalert2';

export const useProvince = () => {
    const [state, dispatch] = useProvinceContext();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchProvinces = useCallback(async () => {
        try {
            setLoading(true);
            const { pagination, sort } = state;

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.get(API_ENDPOINTS.PROVINCES, pageable);
            dispatch({ type: ACTIONS.SET_PROVINCES, payload: data });
            return data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, dispatch, state]);

    const searchProvinces = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            setFilter(searchParams);
            setPage(pageParams);
            return fetchProvinces();
        },
        [setFilter, setPage, fetchProvinces]
    );

    const createProvince = useCallback(
        async (provinceData) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(API_ENDPOINTS.PROVINCES, provinceData);
                dispatch({ type: ACTIONS.ADD_PROVINCE, payload: data });

                showSuccessMessage('Province created successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateProvince = useCallback(
        async (id, provinceData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.PROVINCES}/${id}`, provinceData);
                dispatch({ type: ACTIONS.UPDATE_PROVINCE, payload: { id, data } });

                showSuccessMessage('Province updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const deleteProvince = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.PROVINCES}/${id}`);
                dispatch({ type: ACTIONS.DELETE_PROVINCE, payload: id });

                showSuccessMessage('Province deleted successfully');
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const getProvinceById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.PROVINCES}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_PROVINCE, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const listProvince = useCallback(
        async (page = 0, size = 10) => {
            try {
                setLoading(true);
                const data = await apiCaller.list(API_ENDPOINTS.PROVINCES, page, size);
                dispatch({ type: ACTIONS.SET_PROVINCES, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    return useMemo(
        () => ({
            // State
            provinces: state.provinces,
            selectedProvince: state.selectedProvince,
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            lastUpdated: state.lastUpdated,
            listProvince,

            // List Operations
            setFilter,
            setSort,
            setPage,
            fetchProvinces,
            searchProvinces,

            // CRUD Operations
            createProvince,
            updateProvince,
            deleteProvince,
            getProvinceById,

            // Utilities
            clearError
        }),
        [
            state,
            setFilter,
            setSort,
            setPage,
            fetchProvinces,
            searchProvinces,
            createProvince,
            updateProvince,
            deleteProvince,
            getProvinceById,
            clearError
        ]
    );
};

export default useProvince;
